import { computed, effect, inject, signal, untracked } from '@angular/core';
import type {
  Notification,
  NotificationPreferences,
} from '@shared/models/notification';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@shared/models/notification';
import { signalStore } from '@tvd/cdk/store';
import { AuthService } from '@tvd/data-access/auth';
import { NotificationApi } from './notification-api';
import { NotificationWs } from './notification-ws';
import type { NotificationWsMessage } from './notification-ws';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loaded: boolean;
  preferences: NotificationPreferences;
  preferencesLoaded: boolean;
  showSettings: boolean;
}

const initState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  loaded: false,
  preferences: { ...DEFAULT_NOTIFICATION_PREFERENCES },
  preferencesLoaded: false,
  showSettings: false,
};

export const NotificationStore = signalStore(initState).create(
  (state, patch) => {
    const api = inject(NotificationApi);
    const ws = inject(NotificationWs);
    const auth = inject(AuthService);

    const drawerOpen = signal(false);

    // Connect WebSocket when authenticated
    effect((onCleanup) => {
      const token = auth.token();
      if (!token) {
        ws.destroy();
        return;
      }

      const messages$ = ws.connect();
      if (!messages$) return;

      const sub = messages$.subscribe((message: NotificationWsMessage) => {
        untracked(() => {
          if (message.type === 'connected') {
            patch({ unreadCount: message.unreadCount });
          } else if (message.type === 'notification') {
            const notification = message.notification;
            patch((s) => ({
              ...s,
              notifications: [notification, ...s.notifications],
              unreadCount: s.unreadCount + 1,
            }));
          } else if (message.type === 'unread-count') {
            patch({ unreadCount: message.count });
          }
        });
      });

      onCleanup(() => {
        sub.unsubscribe();
        ws.destroy();
      });
    });

    const loadIfNeeded = () => {
      if (!state().loaded) {
        patch({ loading: true });
        api.list().subscribe({
          next: (res) =>
            patch({
              notifications: res.notifications,
              loading: false,
              loaded: true,
            }),
          error: () => patch({ loading: false }),
        });
      }
    };

    const loadPreferencesIfNeeded = () => {
      if (!state().preferencesLoaded) {
        api.getPreferences().subscribe({
          next: (res) =>
            patch({
              preferences: res.preferences,
              preferencesLoaded: true,
            }),
        });
      }
    };

    const markAllReadInternal = () => {
      const s = state();
      if (s.unreadCount === 0) return;
      const prev = s;
      patch((s) => ({
        ...s,
        notifications: s.notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      }));
      api.markAllRead().subscribe({
        error: () => patch(prev),
      });
    };

    return {
      notifications: computed(() => state().notifications),
      unreadCount: computed(() => state().unreadCount),
      loading: computed(() => state().loading),
      loaded: computed(() => state().loaded),
      preferences: computed(() => state().preferences),
      preferencesLoaded: computed(() => state().preferencesLoaded),
      showSettings: computed(() => state().showSettings),
      drawerOpen,

      toggleDrawer: () => {
        const isOpen = !drawerOpen();
        drawerOpen.set(isOpen);
        if (isOpen) {
          loadIfNeeded();
          markAllReadInternal();
        } else {
          patch({ showSettings: false });
        }
      },

      openDrawer: () => {
        drawerOpen.set(true);
        loadIfNeeded();
        markAllReadInternal();
      },

      closeDrawer: () => {
        drawerOpen.set(false);
        patch({ showSettings: false });
      },

      toggleSettings: () => {
        const show = !state().showSettings;
        patch({ showSettings: show });
        if (show) loadPreferencesIfNeeded();
      },

      markRead: (id: string) => {
        const prev = state();
        patch((s) => ({
          ...s,
          notifications: s.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n,
          ),
          unreadCount: Math.max(0, s.unreadCount - 1),
        }));
        api.markRead(id).subscribe({
          error: () => patch(prev),
        });
      },

      markAllRead: markAllReadInternal,

      updatePreference: (
        key: keyof NotificationPreferences,
        value: boolean,
      ) => {
        const prev = state();
        patch((s) => ({
          ...s,
          preferences: { ...s.preferences, [key]: value },
        }));
        api.updatePreferences({ [key]: value }).subscribe({
          error: () => patch(prev),
        });
      },
    };
  },
);
